import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { format } from 'd3-format';
import * as d3 from 'd3-selection';
import {
  extent as arrayExtent,
  min as arrayMin,
  max as arrayMax,
} from 'd3-array';
import {
  stackOrderAscending,
  area as shapeArea,
  stack as shapeStack,
} from 'd3-shape';
import { scaleOrdinal } from 'd3-scale';
import { timeMinute } from 'd3-time';

import widgetWrapper from './widgetWrapper';

import {
  chartAxis,
  chartAxisLabel,
  chartLegend,
  colorScheme,
  formatTimestamp,
  getCurveType,
  getScaleType,
} from './widgetUtils';

import './AreaChartWidget.scss';

class AreaChartWidget extends Component {

  static propTypes = {
    series: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.number.isRequired })),
      keys: PropTypes.arrayOf(PropTypes.string),
      xDomain: PropTypes.arrayOf(PropTypes.number),
      yDomain: PropTypes.arrayOf(PropTypes.number),
      negativeAreaColor: PropTypes.string,
      curve: PropTypes.string,
      yAxis: PropTypes.shape({
        label: PropTypes.string,
        type: PropTypes.string,
        tickArguments: PropTypes.arrayOf(PropTypes.string),
        tickValues: PropTypes.object,
      }),
      margin: PropTypes.shape({
        top: PropTypes.number,
        right: PropTypes.number,
        left: PropTypes.number,
        bottom: PropTypes.number,
      }),
      points: PropTypes.shape({
        color: PropTypes.string,
        radius: PropTypes.number,
      }),
      colors: PropTypes.arrayOf(PropTypes.string),
    }),
    title: PropTypes.string,
    subtitle: PropTypes.string,
    timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
  };

  static defaultProps = {
    title: '',
    subtitle: '',
    timestamp: '',
    className: '',
    series: {},
  };

  constructor(...args) {
    super(...args);
    this.chartRef = React.createRef();
    this.legendRef = React.createRef();
  }

  componentDidMount() {
    this.doD3Rendering();
  }

  componentDidUpdate(_prevProps, _prevState) {
    this.doD3Rendering();
  }

  doD3Rendering() {
    if (this.props.series) {
      const series = this.props.series;
      const {
        data: dataArray = [],
        keys = ['value'],
        margin: {
          top: marginTop = 10,
          right: marginHeight = 40,
          left: marginLeft = 40,
          bottom: marginBottom = 20,
        } = {},
        points: {
          color: pointColor = null,
          radius: pointRadius = 4,
        } = {},
        colors = [],
      } = series;

      const xAxisProps = Object.assign({
        label: '',
        type: 'time',
        grid: true,
        orient: 'bottom',
        labelPadding: 6,
        tickArguments: [],
        tickValues: null,
        tickFormat: null,
        tickSize: 6,
        tickSizeInner: 6,
        tickSizeOuter: 6,
        tickPadding: 3,
        tickIntervalMinutes: null, // non-d3 field
      }, series.xAxis);

      if (xAxisProps.tickIntervalMinutes) {
        xAxisProps.tickArguments = [timeMinute.every(xAxisProps.tickIntervalMinutes)];
      }

      const yAxisProps = Object.assign({
        label: '',
        type: 'linear',
        grid: true,
        orient: 'left',
        labelPadding: 6,
        tickArguments: [],
        tickValues: null,
        tickFormat: null,
        tickSize: 6,
        tickSizeInner: 6,
        tickSizeOuter: 6,
        tickPadding: 3,
      }, series.yAxis);

      if (typeof xAxisProps.tickFormat === 'string') {
        xAxisProps.tickFormat = format(xAxisProps.tickFormat);
      }
      if (typeof yAxisProps.tickFormat === 'string') {
        yAxisProps.tickFormat = format(yAxisProps.tickFormat);
      }

      // Persist the color scale.
      const scheme = colors instanceof Array && colors.length > 0 ? colors : colorScheme;

      const colorScale = this.colorScale
        || (this.colorScale = scaleOrdinal().range(scheme));
      const negativeAreaColor = series.negativeAreaColor || '#ff0000';

      const xAccessor = (d => d.key);
      const yAccessor = ((d, k) => d[k]);
      const colorAccessor = (k => colorScale(k));
      const getDatumMin = d => Math.min(d[0], d[1]);
      const getDatumMax = d => Math.max(d[0], d[1]);
      const getDatumKey = d => xAccessor(d.data);

      let svg = d3.select(this.chartRef.current)
        .data([0]);
      svg = svg
        .enter()
        .append('svg')
        .attr('class', 'area-chart')
        .merge(svg);

      const legend = d3.select(this.legendRef.current)
        .data([keys.length > 1 ? keys : []]);
      legend.enter()
        .append('ul')
        .attr('class', 'legend')
        .merge(legend)
        .call(chartLegend, colorAccessor);

      const width = svg.node().parentNode.clientWidth;
      const height = svg.node().parentNode.clientHeight;
      const chartWidth = width - marginLeft - marginHeight;
      const chartHeight = height - marginTop - marginBottom;

      svg
        .attr('width', width)
        .attr('height', height);

      // Set the clip path.
      let defs = svg
        .selectAll('defs')
        .data([0]);
      defs = defs
        .enter()
        .append('defs')
        .merge(defs);
      let clipPath = defs
        .selectAll('clipPath')
        .data([0]);
      clipPath = clipPath
        .enter()
        .append('clipPath')
        .merge(clipPath)
        .attr('id', `clip-linechart-${this.chartID}`);
      const clipRect = clipPath
        .selectAll('rect')
        .data([0]);
      clipRect
        .enter()
        .append('rect')
        .merge(clipRect)
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', chartWidth)
        .attr('height', chartHeight);


      // Set the chart margin.
      let margin = svg.selectAll('.margin')
        .data([0]);
      margin = margin.enter()
        .append('g')
        .attr('class', 'margin')
        .merge(margin)
        .attr('transform', `translate(${marginLeft}, ${marginTop})`);

      // Stack the areas with d3.stack().
      const stacked = shapeStack()
        .keys(keys.map(k => (k instanceof Array ? k[0] : k)))
        .value(yAccessor)
        .order(stackOrderAscending)(dataArray)
        .sort((a, b) => a.index - b.index);

      // Examine the data for domains.
      let min;
      let max;
      const xDomain = series.xDomain
        || arrayExtent(dataArray, xAccessor);
      const yDomain = series.yDomain
        || stacked.reduce((e, s) => {
          min = arrayMin(s, getDatumMin);
          max = arrayMax(s, getDatumMax);
          if (e[0] === undefined || min < e[0]) { e[0] = min; }
          if (e[1] === undefined || max > e[1]) { e[1] = max; }
          return e;
        }, []);

      const noData = (xDomain[0] === xDomain[1] || yDomain[0] === yDomain[1]);
      if (noData) {
        margin.append('g')
          .attr('transform', `translate(${chartWidth / 2},${chartHeight / 2})`)
          .append('text')
          .attr('class', 'no-data')
          .attr('text-anchor', 'center')
          .text('NO DATA');
      }

      // Create the scales.
      const xScale = (getScaleType(xAxisProps.type))()
        .domain(xDomain)
        .range([0, chartWidth]);
      const yScale = (getScaleType(yAxisProps.type))()
        .domain(yDomain)
        .range([chartHeight, 0])
        .nice();

      // Set the clip path.
      svg.select('clipPath')
        .attr('id', `clip-areachart-${this.chartID}`)
        .select('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', chartWidth)
        .attr('height', chartHeight);

      // Draw the axes.
      const axis = margin.selectAll('.axis')
        .data([xAxisProps, yAxisProps]);
      axis.enter()
        .append('g')
        .attr('class', (d, i) => `axis ${i ? 'y' : 'x'}`)
        .merge(axis)
        .call(chartAxis, xScale, yScale, !noData);

      // Draw each area.
      const getX = (d, i) => xScale(getDatumKey(d, i));
      const getY0 = (d, i) => yScale(getDatumMin(d, i));
      const getY1 = (d, i) => yScale(getDatumMax(d, i));
      const isPoint = d => d.data.mark;
      const generator = shapeArea()
        .x(getX)
        .y0(getY0)
        .y1(getY1)
        .defined(d => Number.isFinite(d[0]) && (d[1] >= 0))
        .curve(getCurveType(series.curve));

      margin.selectAll('.series').data([]).exit().remove();
      const g = margin.selectAll('.series').data([1, 2]).enter();

      const area = g.append('path')
        .data(stacked, d => d.key);
      area.exit().remove();
      area.enter()
        .append('path')
        .attr('clip-path', `url(#clip-areachart-${this.chartID})`)
        .attr('class', 'series')
        .merge(area)
        .attr('d', generator)
        .style('stroke', 'none')
        .style('stroke-width', 0)
        .style('fill', (d, i) => colorAccessor(d.key, i));

      // Draw the points.
      const points = stacked.reduce((f, s) => {
        s.forEach((d) => { if (isPoint(d)) { f.push(d); } });
        return f;
      }, []);
      const point = margin.selectAll('.point')
        .data(points);
      point.exit().remove();
      point.enter()
        .append('circle')
        .attr('class', 'point')
        .merge(point)
        .attr('cx', getX)
        .attr('cy', getY1)
        .attr('r', pointRadius)
        .style('fill', pointColor);

      // Create the axis labels. They are layered above the chart content.
      const axisLabel = margin.selectAll('.label')
        .data([[xAxisProps, yAxisProps.orient], [yAxisProps, xAxisProps.orient]]);
      axisLabel.enter()
        .append('text')
        .attr('class', (d, i) => `label ${i ? 'y' : 'x'}`)
        .merge(axisLabel)
        .call(chartAxisLabel, xScale, yScale);

      /*
      For the subscribers view at least, we need an area cart showing a SINGLE category,
      which may have negative values to be shaded with a different color.
      Obviously this only makes sense when plotting a SINGLE category
      (i.e. series.keys.length is 1).
      Therefore we only need to specify one color for negative area, rather than an array.
      This is a special element to represent shaded area for any negative areas.
      Outside of the special case where series.keys.length === 1 and negative values are present,
      this element will remain empty.
       */
      const negativeGenerator = shapeArea()
        .x(getX)
        .y0(getY0)
        .y1(getY1)
        .defined(d => Number.isFinite(d[0]) && (d[1] < 0))
        .curve(getCurveType(series.curve));

      const negativeArea = g.append('path')
        .data(stacked, d => d.key);
      negativeArea.exit().remove();
      negativeArea.enter()
        .append('path')
        .attr('clip-path', `url(#clip-areachart-${this.chartId})`)
        .attr('class', 'series')
        .merge(negativeArea)
        .attr('d', negativeGenerator)
        .style('stroke', 'none')
        .style('stroke-width', 0)
        .style('fill', (_d, _i) => negativeAreaColor);
    }
  }

  render() {
    const {
      title = '',
      subtitle = '',
      timestamp = '',
      className = '',
    } = this.props;
    return (
      <div className={`widget-threshold-colors AreaChartWidget ${className}`}>
        <div className="header">
          <div className="title">{title}</div>
          <div className="subtitle">{subtitle}</div>
        </div>
        <div className="content">
          <svg className="area-chart" ref={this.chartRef} />
        </div>
        <div className="footer">
          <ul className="legend" ref={this.legendRef} />
          <div className="timestamp">{formatTimestamp(timestamp)}</div>
        </div>
      </div>
    );
  }
}

export default widgetWrapper(AreaChartWidget);
